import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Checkbox, Form, TextField } from "../../../components/forms";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useGoogleLogin } from "@react-oauth/google";
import {
  useSignUpMutation,
  useSocialSignupMutation,
} from "../../../services/authService";
import { useSubscribeToMailMutation } from "../../../services/dataService";
import { useAuth } from "../../../utils/authUtils";
import parseError from "../../../utils/errorUtils";
import { ACCESS_TOKEN_NAME } from "../../../constants";
import LoadingSpinner from "../../../components/loadingSpinner/LoadingSpinner";
import {
  useUpdateHubSpotMutation
} from "../../../services/dataService"

const EligibilitySuccess = () => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const auth = useAuth();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [signUp] = useSignUpMutation();
  const [signUpWithSocial] = useSocialSignupMutation();
  const [subscribeToMail] = useSubscribeToMailMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [updateHubSpot] = useUpdateHubSpotMutation();

  const toggleModal = () => {
    setShowModal((prev) => !prev);
  };

  const handleNavigate = () =>
    navigate("/onboarding/eligibility-flow/order-confirm", {
      replace: true,
    });

  const eligibilityForm = JSON.parse(
    localStorage.getItem("EligibilityForm") || "{}"
  );

  const [email, setEmail] = useState(eligibilityForm?.["Email address"] || "");
  const [password, setPassword] = useState("");
  const [passwordStrength, setPasswordStrength] = useState([]);
  const fieldsValidity = !email || !password || passwordStrength.length !== 5;

  const requirements = [
    { regex: /.{8,}/, label: "At least 8 characters" },
    { regex: /\d/, label: "At least one number" },
    { regex: /[a-z]/, label: "At least one lowercase letter" },
    { regex: /[^\w\s]/, label: "At least one special character (Eg: @#$!%)" },
    { regex: /[A-Z]/, label: "At least one uppercase letter" },
  ];
  const checkPasswordStrength = (password) => {
    return requirements
      .filter((req) => req.regex.test(password))
      .map((req) => req.label);
  };

  const handleEmailChange = (e) => setEmail(e.target.value);
  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setPasswordStrength(checkPasswordStrength(newPassword));
  };

  const initialValues = {
    email: eligibilityForm?.["Email address"] || "",
    password: "",
    marketingConsent: false,
  };
  const SignInSchema = Yup.object().shape({
    email: Yup.string().email().required("Please enter your email"),
    password: Yup.string()
      .required("Please enter a password")
      .test("password-strength", "Password is not strong enough", (value) => {
        const strength = checkPasswordStrength(value || "");
        setPasswordStrength(strength);
        return strength.length === 5;
      }),
    marketingConsent: Yup.boolean().default(false),
  });

  const handleSubscribeToMail = async (email, name) => {
    try {
      const data = { email, body: name };

      const res = await subscribeToMail(data);

      if (res.error) {
        toast.error(parseError(res.error, "Couldn't subscribe"));
      } else {
        !isMobile && toast.success("Successfully subscribed");
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setIsSubmitting(true);
      const captchaResponseToken = await handleReCaptchaVerify();

      if (values.marketingConsent) {
        await handleSubscribeToMail(
          values.email,
          eligibilityForm?.["First name"] || ""
        );
      }

      const eligibilityQuestionnaire = Object.keys(eligibilityForm).map(
        (key, idx) => {
          return {
            question: key,
            answer: eligibilityForm[key],
          };
        }
      );

      if (captchaResponseToken) {
        let data = {
          ...values,
          username: values.email,
          captchaResponseToken,
          regStatus: "BASIC",
          eligibilityQuestionnaire,
        };

        const res = await signUp({ values: data });

        if (res.error) {
          if (res.error.data?.status === 401) {
            toast.error("Email already exists");
          } else if (res.error.data?.status === 412) {
            toast.error(
              "Your reCAPTCHA verification was failed. Please try again."
            );
          } else {
            toast.error(parseError(res.error, "Couldn't sign up"));
          }
          setIsSubmitting(false);
        } else {
          if (res.data.value) {
            localStorage.setItem(ACCESS_TOKEN_NAME, res.data.value);
            auth.logIn();
            await submitHubSpot(values);
            handleNavigate();
          }
          setIsSubmitting(false);
        }
      } else {
        toast.error("reCAPTCHA failed");
      }
    } catch (error) {
      setIsSubmitting(false);
      console.error(error);
    }
  };

  const handleSocialSignUp = async (values) => {
    try {
      const captchaResponseToken = await handleReCaptchaVerify();

      const eligibilityForm = JSON.parse(
        localStorage.getItem("EligibilityForm") || "{}"
      );

      const eligibilityQuestionnaire = Object.keys(eligibilityForm).map(
        (key, idx) => {
          return {
            question: key,
            answer: eligibilityForm[key],
          };
        }
      );

      if (captchaResponseToken) {
        let data = {
          captchaResponseToken,
          regStatus: "BASIC",
          marketingConsent: false,
          eligibilityQuestionnaire,
        };

        const res = await signUpWithSocial({
          values: data,
          credential: values.token,
          provider: values.provider,
        });

        if (res.error) {
          if (res.error.data?.status === 412) {
            toast.error(
              "Your reCAPTCHA verification was failed. Please try again."
            );
          } else if (res.error.data?.status === 403) {
            toast.error("An account with this email exists. Please login.");
          } else {
            toast.error(parseError(res.error, "Couldn't sign up"));
          }
        } else {
          if (res.data.value) {
            localStorage.setItem(ACCESS_TOKEN_NAME, res.data.value);
            auth.logIn();
            handleNavigate();
          }
        }
      } else {
        toast.error("reCAPTCHA failed");
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleGoogleSignUp = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      const data = {
        token: tokenResponse.access_token,
        provider: "GOOGLE",
      };

      handleSocialSignUp(data);
    },
    onError: (error) => {
      toast.error("Google login failed");
    },
  });

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      return;
    }
    const token = await executeRecaptcha();
    return token;
  }, [executeRecaptcha]);

  useEffect(() => {
    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    setIsMobile(isMobile);
    if (auth?.isLogged) {
      handleNavigate();
    }
    // if (!localStorage.getItem("isOtpVerified")) {
    //   navigate("/onboarding/eligibility-flow/condition");
    // }
  }, []);

  const submitHubSpot = async (values) => {
    const vid = localStorage.getItem("vid")
    try {
      let data = {
        properties: [
          {
            property: "email",
            value: values?.email
          },
          {
            property: "final_status",
            value: "Basic"
          },
          {
            property: "consent_registration",
            value: values?.marketingConsent
          }
        ]
      };
      const updateRes = vid && await updateHubSpot({ values: data, vid: vid })

      if (updateRes.data.status === "Success") {
      } else {
        console.error("hubspot faild")
      }
    } catch (error) {
      console.error(error);
    }
  }

  return (
    <div className="d-flex flex-column h-100">
      <h4 className="mb-2 fw-bold">
        Congratulations, you’re eligible for our services
      </h4>
      <p className="text-gray-1 mb-3">
        The opportunity to improve your well-being is here. Schedule a
        consultation with our specialist doctor - the first step towards renewed
        vitality.
      </p>
      <div className="d-flex flex-column mt-4 h-100">
        <h5 className="mb-2 fw-bold text-primary-600">Create an account</h5>
        <p className="text-gray-1 mb-4 mb-md-3">
          We’ll check if you have an account, and help create one if you don’t.
        </p>
        <Form
          onSubmit={handleSubmit}
          validationSchema={SignInSchema}
          initialValues={initialValues}
          enableReinitialize
          className="d-flex"
        >
          <div className="container px-0 d-flex flex-column justify-content-between">
            <div className="column col-md-12 col-lg-8 col-xl-8">
              <div className="row mb-1 mt-3">
                <div className="col-md-12">
                  <button
                    className="d-flex flex-row align-items-center justify-content-center gap-2 bg-transparent py-2 px-4 w-100 fw-bold fs-6 google-btn rounded-1 text-nowrap"
                    onClick={() => handleGoogleSignUp()}
                  >
                    <img
                      src="/images/vectors/google.svg"
                      alt="alert"
                      className="google-icon"
                    />
                    Sign up with google
                  </button>
                  <div className="d-flex flex-row align-items-center justify-content-center mt-4 mb-3">
                    <hr className="w-100 button-hr" />
                    <h6 className="text-gray-1 px-6 mb-0">OR</h6>
                    <hr className="w-100 button-hr" />
                  </div>
                </div>
              </div>
              <div className="row mb-4">
                <div className="col-md-12">
                  <label className="mb-2">
                    Email<span className="text-red-1">*</span>
                  </label>
                  <TextField
                    name="email"
                    type="email"
                    onChange={handleEmailChange}
                    placeholder="Enter your email address"
                    autoComplete="no"
                    className="p-2 px-3 fs-6 bg-transparent border card-border rounded custom-placeholder "
                  />
                </div>
              </div>
            </div>
            <div className="column col-md-12 col-lg-12 col-xl-12">
              <div className="row align-items-start">
                <div className="col-md-12 col-lg-8 col-xl-8 password-row">
                  <div className="row mb-3">
                    <div className="col-md-12 col-xl-12">
                      <label className="mb-2">
                        Password<span className="text-red-1">*</span>
                      </label>
                      <TextField
                        name="password"
                        type="password"
                        onChange={handlePasswordChange}
                        placeholder="Enter your password"
                        autoComplete="new-password"
                        className="p-2 px-3 fs-6 bg-transparent border card-border rounded custom-placeholder"
                      />
                    </div>
                    <div className="d-flex flex-row justify-content-between gap-4 gap-md-12 align-items-center justify-content-center mt-2">
                      <div className="d-flex flex-row gap-2 w-100">
                        {[...Array(5)].map((_, index) => (
                          <hr
                            key={index}
                            className={`w-100 rounded ${index < passwordStrength.length
                              ? "password-valid"
                              : "password-hr"
                              }`}
                          />
                        ))}
                      </div>
                      <p className="mb-0 text-primary-600" onClick={toggleModal}>
                        {passwordStrength.length === 5 ? "Strong" : "Weak"}
                      </p>
                    </div>
                    <div className="col-11 d-flex d-lg-none password-info-div rounded px-4 py-4 ms-4 mt-3">
                      <div className="d-flex flex-column justify-content-between">
                        <ul className="d-flex flex-column gap-2 pathway-list mb-0">
                          {requirements.map((req, index) => (
                            <li
                              key={index}
                              className={`mb-1 ${passwordStrength.includes(req.label)
                                ? "text-success"
                                : "text-dark"
                                }`}
                            >
                              <span className="me-2">✔</span>
                              {req.label}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                  <div className="row mb-4">
                    <div className="col-md-12">
                      <Checkbox
                        label="I would like to receive the latest offers, promotions, and general medical information from Treat It."
                        name="marketingConsent"
                        className="check-lg"
                        inputClass="rounded"
                        labelClass="ms-3"
                      />
                    </div>
                  </div>

                  <div className="row mb-5 mt-6">
                    <div className="col-md-12">
                      <button
                        className={`bg-primary-600 text-white py-2 px-4 w-100 fw-bold fs-6 border-0 rounded text-nowrap ${fieldsValidity
                            ? "opacity-50 cursor-not-allowed"
                            : "opacity-100 cursor-pointer"
                          }`}
                        type="submit"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <LoadingSpinner loadingText="Create an account" />
                        ) : (
                          "Create an account"
                        )}
                      </button>

                      <p className="privacy-text mb-0 mt-5 mt-md-4">
                        By creating your account you confirm you have read and
                        accepted the{" "}
                        <a
                          href="https://treat-it.clinic/privacy-policy/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 fw-semibold text-decoration-underline"
                        >
                          Privacy Policy
                        </a>{" "}
                        and the{" "}
                        <a
                          href="https://treat-it.clinic/terms-conditions/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 fw-semibold text-decoration-underline"
                        >
                          Terms and Conditions.
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-md-12 col-lg-4 col-xl-4 d-none d-lg-block pt-6">
                  <div className="password-info-div rounded px-4 py-4 ms-4">
                    <div className="d-flex flex-column justify-content-between">
                      <ul className="d-flex flex-column gap-2 pathway-list mb-0">
                        {requirements.map((req, index) => (
                          <li
                            key={index}
                            className={`mb-1 ${passwordStrength.includes(req.label)
                              ? "text-success"
                              : "text-dark"
                              }`}
                          >
                            <span className="me-2">✔</span>
                            {req.label}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default EligibilitySuccess;
